import { StrictMode } from 'react';
import { Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { createRoot } from 'react-dom/client';
import { AppProviders } from './app/providers/AppProviders';
import { AppRouter, router } from './app/router';
import './shared/i18n/config';
import { probeOnReturn } from './shared/lib/net';
import { initNativeBack } from './shared/lib/nativeBack';
import { initKeyboard } from './shared/lib/nativeChrome';
import { hydrateFromDisk } from './shared/lib/durableStore';
import { ensureSplashClosing } from './shared/lib/splashNativo';

/**
 * PORTA L'INDIRIZZO A `/` QUANDO LA PAGINA E' UN FILE.
 *
 * ⚠️ La WebView carica `https://localhost/index.html` — e' il `<content src="index.html" />` del
 * `config.xml`, e non si puo' evitare: un file ha un nome. Il router legge `location.pathname`,
 * quindi vede `/index.html`, per cui **non esiste nessuna rotta**: la prima schermata dopo
 * l'accesso era un 404.
 *
 * Sul web non si vede perche' nginx serve `/`: il difetto vive solo dove la pagina e' un file,
 * cioe' dentro l'APK.
 *
 * ⚠️ `search` e `hash` si **conservano**. Non e' prudenza generica: il ritorno dall'accesso
 * Google porta un OTP monouso nella query, e buttarlo qui vorrebbe dire un accesso che non
 * finisce — con l'utente che rivede la schermata di login dopo aver dato il consenso.
 *
 * ⚠️ `replaceState` e non `pushState`: con il secondo il tasto Indietro tornerebbe su
 * `/index.html`, cioe' sul 404 appena evitato.
 */
function normalizeAddress(): void {
  if (typeof window === 'undefined') return;
  const { pathname, search, hash } = window.location;
  if (!pathname.endsWith('/index.html')) return;
  const radice = pathname.slice(0, -'index.html'.length) || '/';
  window.history.replaceState(null, '', `${radice}${search}${hash}`);
}

/**
 * ASPETTA `deviceready`, SE SIAMO IN UN GUSCIO CORDOVA.
 *
 * ⚠️ `window.cordova` esiste **appena `cordova.js` e' stato eseguito**, quindi `runtimeNativo()`
 * risponde giusto da subito. Gli **oggetti dei plugin** no: `navigator.camera`,
 * `navigator.splashscreen`, `window.Fingerprint`, `window.FirebasePlugin` li installa Cordova
 * durante l'avvio e sono pronti solo a `deviceready`. Chiamarli prima non da' un errore: da'
 * `undefined`, che con gli optional call di questi file diventa un no-op **silenzioso**.
 *
 * ⚠️ C'e' un tetto di tempo, e non e' pignoleria: se `deviceready` non arrivasse — guscio
 * confezionato male, plugin che va in errore all'avvio — aspettarlo per sempre vorrebbe dire
 * un'applicazione che non parte mai. Meglio partire senza i plugin che non partire: e' la
 * stessa scelta del tetto sotto lo splash, un piano piu' su.
 */
const DEVICEREADY_CAP_MS = 5_000;

function whenReady(): Promise<void> {
  const w = window as { cordova?: unknown };
  if (!w.cordova) return Promise.resolve();
  return new Promise((resolve) => {
    let done = false;
    const finished = () => { if (!done) { done = true; resolve(); } };
    document.addEventListener('deviceready', finished, { once: true });
    setTimeout(finished, DEVICEREADY_CAP_MS);
  });
}

/* Tasto Indietro di Android agganciato al router, e tastiera che restringe la WebView invece di
   coprirla. Sul web sono due no-op: nessun ramo in più da mantenere.

   ⚠️ Qui c'era anche `initNet()`, che leggeva lo stato di rete dal sistema operativo con
   il plugin di rete del guscio precedente. E' sparito con lui: qui non c'e' un plugin di rete,
   e il segnale che conta lo da' la sonda (vedi `shared/lib/net.ts`). */
/* La sonda al ritorno in primo piano: e' il caso che nessun evento di rete copre — il telefono
   e' stato in tasca per ore, la rete e' cambiata tre volte, e la pagina non era viva per sentire
   `online`. Al ritorno non sappiamo niente, e la coda delle timbrature potrebbe avere righe da
   mandare. Non si disiscrive: vive quanto l'applicazione. */
probeOnReturn();
/* ⚠️ Il `canGoBack` glielo passiamo noi, perche' l'unico che lo sa e' il router: in Cordova
   l'evento `backbutton` non porta niente, e il ripiego `history.length > 1` sbagliava proprio il
   caso della home — dopo l'accesso quel numero e' maggiore di uno, quindi il tasto tornava
   indietro invece di uscire, e a schermo non succedeva niente. */
initNativeBack({
  canGoBack: () => router.history.canGoBack(),
  /*
   * ⚠️ Si **chiede** prima di uscire, ed e' il comportamento di Lares
   * (`chiediSeUscire`): chi premeva Indietro sulla home si ritrovava l'app in secondo piano
   * senza aver deciso niente, e con un tocco per sbaglio quello e' un'uscita involontaria.
   *
   * ⚠️ `modals` si usa **fuori da React**, e funziona perche' e' un archivio globale:
   * `ModalsProvider` e' montato in `AppProviders`, e qui si chiama solo quando l'utente premi
   * il tasto — cioe' molto dopo. Se il provider non fosse montato la chiamata non farebbe
   * niente e l'app non uscirebbe: e' un ripiego silenzioso, quindi va detto.
   */
  onExit: (exit) => {
    modals.openConfirmModal({
      title: 'Uscire dall’applicazione?',
      children: <Text size="sm">Le timbrature non inviate restano sul telefono e partono al prossimo avvio.</Text>,
      labels: { confirm: 'Esci', cancel: 'Resta' },
      confirmProps: { color: 'red' },
      onConfirm: exit,
    });
  },
});
initKeyboard();
/* L'ultima rete sotto lo splash: `AutoHideSplashScreen` in `cordova/config.xml` e' `false`, cioe'
   la schermata d'avvio la deve chiudere l'applicazione. Se non ci arrivasse — errore durante il
   mount di React, chunk mancante — l'app resterebbe su un'immagine per sempre. Questo tetto la
   toglie comunque.

   ⚠️ Il velo animato **esiste**, ed e' `shared/components/SplashAnimato.tsx`: il logo maeway in
   3D (WebGL), montato a tutto schermo da `AppProviders` appena React parte. Il 09-09-2026 questo
   commento e' stato riscritto **due volte**, e la prima riscrittura diceva che quel velo non
   c'era — sbagliato, e affermato con sicurezza dopo aver cercato la parola «velo» invece del
   componente. Una ricerca che non trova non e' una prova che la cosa non ci sia.

   All'avvio si vedono quindi **due** animazioni in fila, di due mondi diversi:
   1. quella del **sistema**, che anima l'icona ferma (un PNG: vedi le preferenze
      `AndroidWindowSplashScreen*` in `cordova/config.xml`);
   2. quella **nostra**, il logo che gira — che chiude subito lo splash di sistema, perche' sotto
      c'e' gia' lo stesso marchio, e se ne va al termine del ciclo o dopo tre secondi.

   Questo tetto e' la rete **sotto** entrambe: se React non montasse affatto, nessuno dei due si
   chiuderebbe. */
ensureSplashClosing();

const root = document.getElementById('root');
if (!root) throw new Error('Root element not found');

/*
 * Il montaggio aspetta il ripristino della coda timbrature dal disco.
 *
 * La coda viene letta al primo render: se il sistema ha ripulito il `localStorage` mentre l'app era
 * chiusa — su Android succede quando lo spazio scarseggia — idratare **dopo** significherebbe
 * mostrare «nessuna timbratura in attesa» a chi ne ha tre, e lasciargliele scoprire per caso.
 *
 * Sul web `idrataDaDisco` non fa niente e risolve subito: l'attesa è un microtask, non un ritardo.
 */
/*
 * ⚠️ `quandoPronto()` davanti a tutto: prima di questa riga il montaggio partiva subito, e in un
 * guscio Cordova i rami nativi trovavano gli oggetti dei plugin ancora assenti. Sul web risolve
 * immediatamente, quindi non aggiunge un'attesa a nessuno.
 */
/* Prima di tutto: il router legge l'indirizzo al primo render, e va normalizzato **prima**. */
normalizeAddress();

void whenReady()
  .then(() => hydrateFromDisk())
  .finally(() => {
  createRoot(root).render(
    <StrictMode>
      <AppProviders>
        <AppRouter />
      </AppProviders>
    </StrictMode>
  );
});
